<template>
  <div class="chart-container">
    <Loading v-show="!loaded" />
    <div v-show="loaded" ref="chart" class="chart">
      <div 
        v-if="selectedTick" 
        class="tooltip"
        :style="{ top: `${selectedTick.cursor.y}px`, left: `${selectedTick.cursor.x}px` }"
      >
        <span>
          {{ selectedTick.y1 }} - {{ selectedTick.y2 }}
        </span>
        <span v-if="selectedTick.play">
          {{ selectedTick.play.crono }} ({{ getPeriodStr(selectedTick.play.periodo) }})
        </span>
        <span v-else>
          {{ parseSecondToTime(selectedTick.x) }}
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import * as d3 from 'd3';

import api from "@/api/index";
import Loading from "@/components/subcomponents/general/Loading.vue";
import {
  FREE_THROW_MADE_EVENT_ID,
  TWO_POINTS_SHOT_MADE_EVENT_ID,
  THREE_POINTS_SHOT_MADE_EVENT_ID,
  DUNK_EVENT_ID,
  END_MATCH_EVENT_ID
} from "@/utils/events";

export default {
  name: "PointsDifferenceChart",
  data() {
    const margin = { top: 80, right: 20, bottom: 80, left: 60 };

    return {
      loaded: false,
      pointsPlays: [],
      chartSettings: {
        svgReference: null,
        xReference: null,
        yReference: null,
        margin: margin,
        width: 960 - margin.left - margin.right,
        height: 500 - margin.top - margin.bottom,
        localLineColor: '#4B4B4B',
        visitorLineColor: '#808080',
        maxPointsDrawed: 0,
      },
      selectedTick: null,
    };
  },
  computed: {
    refreshSignal() {
      return this.$store.state.general.refreshSignal;
    },
    isSmall() {
      return this.$store.state.general.isSmall;
    }
  },
  methods: {
    showTooltip(tick) {
      if (
        this.$store.state.general.isApp
        || this.$store.state.general.isSmall
      ) {
        return;
      }

      if (tick.cursor.x > this.chartSettings.width / 2) {
        tick.cursor.x -= 125;
      }

      if (tick.cursor.y > (this.$refs.chart.clientHeight - 40)) {
        tick.cursor.y = this.$refs.chart.clientHeight - 50;
      }

      this.selectedTick = tick;
    },
    hideTooltip() {
      this.selectedTick = null;
    },
    getMaxPoints() {
      const maxPoints = Math.max(
        ...this.pointsPlays.map(tick => tick.play?.puntosLocal || 0),
        ...this.pointsPlays.map(tick => tick.play?.puntosVisitante || 0)
      );
      return maxPoints;
    },
    getLastPeriod() {
      return this.pointsPlays[this.pointsPlays.length - 1].play?.periodo;
    },
    loadPointsDifferenceChart() {
      api.match.getPlaybyplays(this.$route.params.id_match).then(response => {
        const ticks = 
          response
            .filter(play => [FREE_THROW_MADE_EVENT_ID, TWO_POINTS_SHOT_MADE_EVENT_ID, THREE_POINTS_SHOT_MADE_EVENT_ID, DUNK_EVENT_ID].includes(play.idPlayByPlayType))
            .reverse()
            .map((play) => ({
              play,
              x: this.parseTimeToSeconds(play.periodo, play.minute, play.second),
              y1: play.puntosLocal,
              y2: play.puntosVisitante,
            }));

        // add 0, 0 to first data point
        ticks.unshift({ x: 0, y1: 0, y2: 0 });

        const endMatch = response.find(play => play.idPlayByPlayType === END_MATCH_EVENT_ID);
        if (endMatch) {
          ticks.push({ 
            play: endMatch,
            x: this.parseTimeToSeconds(endMatch.periodo, endMatch.minute, endMatch.second),
            y1: endMatch.puntosLocal,
            y2: endMatch.puntosVisitante,
          });
        }
      
        this.$data.pointsPlays = ticks;
        this.$data.loaded = true;
        this.createChart();        

        this.resizeChartFont();
      });
    },
    updatePointsDifferenceChart() {
      api.match.getPlaybyplays(this.$route.params.id_match).then(response => {
        const newPointsPlays = 
          response
            .filter(play => [FREE_THROW_MADE_EVENT_ID, TWO_POINTS_SHOT_MADE_EVENT_ID, THREE_POINTS_SHOT_MADE_EVENT_ID, DUNK_EVENT_ID].includes(play.idPlayByPlayType))
            .reverse()
            .map((play) => ({
              play,
              x: this.parseTimeToSeconds(play.periodo, play.minute, play.second),
              y1: play.puntosLocal,
              y2: play.puntosVisitante,
            }));

        const endMatch = response.find(play => play.idPlayByPlayType === END_MATCH_EVENT_ID);
        if (endMatch) {
          newPointsPlays.push({ 
            play: endMatch,
            x: this.parseTimeToSeconds(endMatch.periodo, endMatch.minute, endMatch.second),
            y1: endMatch.puntosLocal,
            y2: endMatch.puntosVisitante,
          });
        }

        const lastPeriodDrawed = this.getLastPeriod();
        const newPlays = [];
        let playsChanged = false;        

        newPointsPlays.forEach(newTick => {
          const index = this.pointsPlays.findIndex(oldTick => oldTick?.play && oldTick.play.order === newTick.play.order);
          if (index !== -1) {
            if (
              this.pointsPlays[index].x !== newTick.x ||
              this.pointsPlays[index].y1 !== newTick.y1 ||
              this.pointsPlays[index].y2 !== newTick.y2
            ) {
              playsChanged = true;
              this.pointsPlays.splice(index, 1, newTick);
            }
          } else {
            newPlays.push(newTick);
          }
        });

        this.pointsPlays.push(...newPlays);

        const deletedPlays = this.pointsPlays.filter(oldTick => {
          if (!oldTick.play) {
            return false;
          }

          return !newPointsPlays.some(newTick => newTick.play.order === oldTick.play.order);
        });

        deletedPlays.forEach(deletedTick => {
          this.pointsPlays.splice(this.pointsPlays.indexOf(deletedTick), 1);
          playsChanged = true;
        });
        
        const newLastPeriod = this.getLastPeriod();

        if (
          lastPeriodDrawed !== newLastPeriod
          || this.$data.chartSettings.maxPointsDrawed < this.getMaxPoints() + 3
          || playsChanged
        ) {
          this.rehidrateAxisChart();
          this.rehidratePoints();
          return;
        }

        if (newPlays.length === 0) {
          return;
        }

        this.drawPoints(newPlays);
      });
    },
    parseTimeToSeconds(period, minute, second) {
      // Calculate the time in seconds from the start of the game
      // It's necessary because the minute and second are decreasing ex: minute 8, second 30 => 1minute 30 seconds in x chart
      const currentPeriod = period <= 4 ? 10 : 5;
      let lastPeriodInSeconds = 0;
      for (let i = 1; i < period; i++) {
        const minutesPeriods = i <= 4 ? 10 : 5;
        lastPeriodInSeconds += minutesPeriods * 60;
      }

      const timeToAdd = currentPeriod * 60 - (minute * 60 + second);
      return lastPeriodInSeconds + timeToAdd;
    },
    parseSecondToTime(seconds) {
      const minutes = Math.floor(seconds / 60);
      const secondsLeft = seconds % 60;
      return `${minutes}:${secondsLeft < 10 ? '0' : ''}${secondsLeft}`;
    },
    resizeChartFont() {
      const width = this.$refs.chart.clientWidth;

      if (width === 0) {
        setTimeout(this.resizeChartFont, 100);
      }

      clearTimeout(this.resizeChartFont);

      let baseFontSize = 12;
      if (width < 450) {
        baseFontSize = 10;
      } else if (width < 600) {
        baseFontSize = 11;
      }

      const fontSize = baseFontSize * (this.$data.chartSettings.width / width);
      this.$data.chartSettings.svgReference.selectAll('text').style('font-size', `${fontSize}px`);
    },
    createChart() {
      this.$data.chartSettings.svgReference = d3.select(this.$refs.chart)
        .append('svg')
        .attr("preserveAspectRatio", "xMinYMin meet")
        .attr("viewBox", "0 0 960 500")
        .append('g')
        .attr('transform', 'translate(' + this.$data.chartSettings.margin.left + ',' + this.$data.chartSettings.margin.top + ')');

      d3.select(window).on("resize", this.resizeChartFont);

      this.rehidrateAxisChart();
      this.rehidratePoints();
    },
    clearChart() {
      this.$data.chartSettings.svgReference.selectAll("*").remove();
    },
    rehidrateAxisChart() {
      // clear the svg
      this.clearChart();

      this.$data.chartSettings.xReference = d3.scaleLinear()
        .domain([0, this.parseTimeToSeconds(this.getLastPeriod(), 0, 0)])
        .range([0, this.chartSettings.width]);

      this.$data.chartSettings.maxPointsDrawed = this.getMaxPoints() + 12;

      this.$data.chartSettings.yReference = d3.scaleLinear()
        .domain([0, this.$data.chartSettings.maxPointsDrawed])
        .range([this.chartSettings.height, 0]);

      let tickGap = 120;
      if (document.body.clientWidth < 740) {
        tickGap = 240;
      }
      tickGap = d3.range(0, this.parseTimeToSeconds(this.getLastPeriod(), 0, 0) + 1, tickGap).length > 24 ? 480 : tickGap;

      this.chartSettings.svgReference
        .append('g')
        .attr('class', 'x axis')
        .attr("transform", `translate(0,${this.chartSettings.height})`)
        .call(
          d3.axisBottom(this.$data.chartSettings.xReference)
            .tickValues(d3.range(0, this.parseTimeToSeconds(this.getLastPeriod(), 0, 0) + 1, tickGap))
            .tickFormat(d => {
              const minutes = Math.floor(d / 60);
              // const seconds = d % 60;
              // return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
              return `${minutes}`;
            })           
        );

      const ticks = [];
      let iterate = 10;

      if (this.isSmall) {
        iterate = this.$data.chartSettings.maxPointsDrawed > 50 ? 15 : this.$data.chartSettings.maxPointsDrawed / 5;
      } else {
        iterate = this.$data.chartSettings.maxPointsDrawed > 50 ? 10 : this.$data.chartSettings.maxPointsDrawed / 8;
      }
      
      for (let x=0; x <= this.$data.chartSettings.maxPointsDrawed; x += iterate) {
        ticks.push(x);
      }

      this.chartSettings.svgReference
        .append('g')
        .attr('class', 'y axis')
        .call(
          d3.axisLeft(this.$data.chartSettings.yReference)
            .tickValues(ticks)
        );

      const xLegendSettings = {
        rotate: this.isSmall ? 0 : 90,
        x: this.isSmall ? 0 - this.chartSettings.margin.left : this.chartSettings.height,
        y: this.isSmall ? 0 - this.chartSettings.margin.top : 0 + this.chartSettings.margin.left / 2,
        anchor: this.isSmall ? 'start' : 'end',
      }

      this.chartSettings.svgReference
        .append('text')
        .attr('transform', `rotate(${xLegendSettings.rotate})`)
        .attr('y', xLegendSettings.y)
        .attr('x', xLegendSettings.x)
        .attr('dy', '1em')
        .attr("font-weight", "bold")
        .style('text-anchor', xLegendSettings.anchor)
        .text(this.$i18n.t("Points"));

      this.chartSettings.svgReference
        .append('text')
        .attr('y', this.chartSettings.height + this.chartSettings.margin.bottom / 2)
        .attr('x', 0)
        .attr('dy', '1em')
        .attr("font-weight", "bold")
        .style('text-anchor', 'start')
        .text(this.$i18n.t("Minutes"));

      // draw vertical lines for each first minute of the period
      this.chartSettings.svgReference
        .append("g")
        .selectAll("rect")
        .data(d3.range(1, this.getLastPeriod() + 1))
        .join("rect")
          .attr('fill', 'black')
          .attr('opacity', 0.25)
          .attr("x", d => this.chartSettings.xReference(this.parseTimeToSeconds(d, 0, 0)))
          .attr("y", 0)
          .attr("width", 1)
          .attr("height", this.chartSettings.height);
    },
    rehidratePoints() {
      const line1 = d3.line()
        .x(d => this.$data.chartSettings.xReference(d.x))
        .y(d => this.$data.chartSettings.yReference(d.y1));

      this.chartSettings.svgReference
        .append('path')
        .data([this.pointsPlays])
        .attr("d", line1)
        .attr("stroke", this.chartSettings.localLineColor)
        .attr("fill", "none")
        .attr("stroke-width", 2);

      const line2 = d3.line()
        .x(d => this.$data.chartSettings.xReference(d.x))
        .y(d => this.$data.chartSettings.yReference(d.y2));

      this.chartSettings.svgReference
        .append('path')
        .data([this.pointsPlays])
        .attr("d", line2)
        .attr("stroke", this.chartSettings.visitorLineColor)
        .attr("fill", "none")
        .attr("stroke-width", 2);

      this.drawCircle();
      this.drawVerticalLine();
      this.drawHoverZone();      
    },
    drawPoints(ticks) {
      //Draw only new ticks, keeping axis and old ticks
      const lastTick = this.$data.pointsPlays[this.$data.pointsPlays.length - ticks.length - 1];

      const line1 = d3.line()
        .x(d => this.$data.chartSettings.xReference(d.x))
        .y(d => this.$data.chartSettings.yReference(d.y1));

      this.chartSettings.svgReference
        .append('path')
        .data([[lastTick, ...ticks]])
        .attr("d", line1)
        .attr("stroke", this.chartSettings.localLineColor)
        .attr("fill", "none")
        .attr("stroke-width", 2);

      const line2 = d3.line()
        .x(d => this.$data.chartSettings.xReference(d.x))
        .y(d => this.$data.chartSettings.yReference(d.y2));

      this.chartSettings.svgReference
        .append('path')
        .data([[lastTick, ...ticks]])
        .attr("d", line2)
        .attr("stroke", this.chartSettings.visitorLineColor)
        .attr("fill", "none")
        .attr("stroke-width", 2);

      this.drawCircle([...ticks]);
      this.drawVerticalLine([...ticks]);
      this.drawHoverZone();
    },
    drawCircle(data = this.pointsPlays) {     
      // make circles on every point team 1
      this.chartSettings.svgReference
        .append("g")
        .selectAll("circle")
        .data(data)
        .join("circle")
          .attr("fill", this.chartSettings.localLineColor)
          .attr("opacity", 0)
          .attr("data-index", (d) => `circle-index-${d.x}`)
          .attr("data-team", "local")
          .attr("cx", d => this.chartSettings.xReference(d.x))
          .attr("cy", d => this.chartSettings.yReference(d.y1))
          .attr("r", 5);

      // make circles on every point team 2
      this.chartSettings.svgReference
        .append("g")
        .selectAll("circle")
        .data(data)
        .join("circle")
          .attr("fill", this.chartSettings.visitorLineColor)
          .attr("opacity", 0)
          .attr("data-index", (d) => `circle-index-${d.x}`)
          .attr("data-team", "visitor")
          .attr("cx", d => this.chartSettings.xReference(d.x))
          .attr("cy", d => this.chartSettings.yReference(d.y2))
          .attr("r", 5);

      // remove all repeated circle in local team and the same x point
      this.chartSettings.svgReference
        .selectAll('[data-team="local"]')
        .data(data)
        .filter((d, i, nodes) => {
          const nextNode = nodes[i + 1];
          return nextNode && nextNode.__data__.x === d.x;
        })
        .remove();

      // remove all repeated circle in visitor team and the same x point
      this.chartSettings.svgReference
        .selectAll('[data-team="visitor"]')
        .data(data)
        .filter((d, i, nodes) => {
          const nextNode = nodes[i + 1];
          return nextNode && nextNode.__data__.x === d.x;
        })
        .remove();
    },
    drawVerticalLine(data = this.pointsPlays) {
      // draw vertical cursor lines on every point
      this.chartSettings.svgReference
        .append("g")
        .selectAll("rect")
        .data(data)
        .join("rect")
          .attr('fill', 'black')
          .attr('opacity', 0)
          .attr('data-index', (d) => `line-index-${d.x}`)
          .attr("x", d => this.chartSettings.xReference(d.x))
          .attr("y", 0)
          .attr("width", 1)
          .attr("height", this.chartSettings.height);
    },
    drawHoverZone() {
      // transparent rectangle to make the hover effect to display vertical line, circles and tooltip
      this.chartSettings.svgReference
        .append("g")
        .attr('fill', 'black')
        .attr('opacity', 0)
        .selectAll("rect")
        .data(this.pointsPlays)
        .join("rect")
          .attr("x", (d, i) => {
            const previusTick = this.pointsPlays[i - 1];

            if (!previusTick) {
              return 0;
            }
            
            const negativeLeft = (this.chartSettings.xReference(d.x) - this.chartSettings.xReference(previusTick.x)) / 2;
            
            return this.chartSettings.xReference(d.x) - negativeLeft;
          })
          .attr("y", 0)
          .attr("width", (d, i) => {
            const nextTick = this.pointsPlays[i + 1];

            if (!nextTick) {
              return this.chartSettings.xReference(d.x);
            }

            const positiveLeft = (this.chartSettings.xReference(nextTick.x) - this.chartSettings.xReference(d.x)) / 2;

            return this.chartSettings.xReference(nextTick.x) + positiveLeft;
          })
          .attr("height", this.chartSettings.height)
          .on("mouseover", (event, d) => {
            const yTooltip = d.y1 > d.y2 ? this.chartSettings.yReference(d.y1) : this.chartSettings.yReference(d.y2)

            d.cursor = {
              x: event.offsetX + 25,
              y: yTooltip + 25,
            }

            document.querySelector(`[data-index="line-index-${d.x}"]`).style.opacity = 1;

            document.querySelectorAll(`[data-index="circle-index-${d.x}"]`).forEach(circle => {
              circle.style.opacity = 1;
            });

            this.showTooltip(d);
          })
          .on("mouseout", () => {
            document.querySelectorAll('[data-index^="line-index-"]').forEach(line => {
              line.style.opacity = 0;
            });

            document.querySelectorAll('[data-index^="circle-index-"]').forEach(circle => {
              circle.style.opacity = 0;
            });

            this.hideTooltip();
          });
    },
    getPeriodStr(period) {
			if (period < 5) {
				return period + this.$i18n.t("Q");
			} else {
				return `${this.$i18n.t("OT")}${period - 4}`;
			}
		},
  },
  watch: {
    refreshSignal(newValue) {
      if (!newValue) {
        return;
      }

      if (this.$store.state.match.status == "playing" || this.$store.state.general.refreshing) {
        this.updatePointsDifferenceChart();
      }
    },
    isSmall() {
      this.rehidrateAxisChart();
      this.rehidratePoints();
    }
  },
  created() {
    this.loadPointsDifferenceChart();
  },
  components: {
    Loading,
  },
}
</script>

<style>
.chart-container {
  width: 100%;
  max-width: 900px;
}

.chart {
  position: relative;
}

.chart text {
  font-size: 1rem;
}

.tooltip {
  position: absolute;
}
</style>